import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, Wifi, Activity, CheckCircle, XCircle, Crown, Gift, Zap, TrendingUp, AlertCircle, Infinity as InfinityIcon, CircleCheckBig, Bot, Pause, Play, X, SlidersHorizontal, MessageCircle, ListChecks, Timer, Megaphone, CalendarClock, Check } from 'lucide-react';
import { MessageLog } from '../types';
import { getSubscriptionLimits, getCurrentUser, getUserStats } from '../services/usersApi';
import { updateBotPauseSettings } from '../services/sessionsApi';
import { SessionManager, SessionManagerHandle } from './SessionManager';
import { useSession } from '../context/SessionContext';
import { toast } from 'sonner';

type PauseMode = 'all' | 'conversation' | 'custom';

const pauseFeatureOptions = [
  { id: 'autoReplies', label: 'Auto-respuestas e IA', description: 'Reglas de respuesta, asistentes IA y validaciones automáticas.', icon: MessageCircle },
  { id: 'menus', label: 'Menús interactivos', description: 'Inicio de menús, navegación y respuestas a sus opciones.', icon: ListChecks },
  { id: 'followUps', label: 'Seguimientos automáticos', description: 'Mensajes posteriores generados por asistentes IA.', icon: Timer },
  { id: 'bulk', label: 'Envíos masivos', description: 'Pausa la cola de campañas hasta reanudarla.', icon: Megaphone },
  { id: 'scheduled', label: 'Mensajes programados', description: 'Mantiene los mensajes en espera mientras dure la pausa.', icon: CalendarClock },
] as const;

const pauseFeatureLabels: Record<string, string> = {
  autoReplies: 'Auto-respuestas e IA',
  menus: 'Menús interactivos',
  followUps: 'Seguimientos automáticos',
  bulk: 'Envíos masivos',
  scheduled: 'Mensajes programados',
};

const getPauseMode = (features: string[]): PauseMode | null => {
  if (features.includes('all')) return 'all';
  if (features.length === 0) return null;
  if (features.length === 2 && features.includes('autoReplies') && features.includes('menus')) return 'conversation';
  return 'custom';
};

interface DashboardProps {
  isConnected: boolean;
  logs: MessageLog[];
  qrCode?: string | null;
  onInitialize: () => void;
  onResetSession?: () => void;
  isInitializing: boolean;
  socketStatus?: string;
}

interface DashboardPropsWithUser extends DashboardProps {
  currentUserId?: number | null;
  currentUser?: any;
  connectedPhone?: string | null;
}

export const Dashboard: React.FC<DashboardPropsWithUser> = ({ logs, onInitialize, onResetSession, socketStatus, currentUserId, currentUser: propCurrentUser, connectedPhone }) => {
  const { sessions, selectedSession, selectedSessionId, loading: sessionsLoading, initializeSession, refreshQR, loadSessions } = useSession();
  const isConnected = selectedSession?.isReady || false;
  const qrCode = selectedSession?.status === 'waiting_qr' ? '(Cargando QR...)' : null; // QR handling will be via SessionManager or refreshQR
  console.log('Dashboard render:', { isConnected, hasQrCode: !!qrCode, qrCodeLength: qrCode?.length, socketStatus });

  const [userInfo, setUserInfo] = useState<any>(null);
  const [subscriptionLimits, setSubscriptionLimits] = useState<any[]>([]);
  const [userStats, setUserStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const sessionManagerRef = useRef<SessionManagerHandle>(null);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [pauseMode, setPauseMode] = useState<PauseMode>('conversation');
  const [customPauseFeatures, setCustomPauseFeatures] = useState<string[]>([]);
  const [savingPause, setSavingPause] = useState(false);

  const pausedFeatures = selectedSession?.pausedFeatures || [];
  const pauseIsActive = pausedFeatures.length > 0;

  const openPauseDialog = () => {
    const currentMode = getPauseMode(pausedFeatures);
    setPauseMode(currentMode || 'conversation');
    setCustomPauseFeatures(currentMode === 'custom' ? pausedFeatures : []);
    setShowPauseDialog(true);
  };

  const savePauseSettings = async (features: string[]) => {
    if (!selectedSessionId) {
      toast.error('Selecciona una sesión para configurar la pausa.');
      return;
    }

    setSavingPause(true);
    try {
      const response = await updateBotPauseSettings(selectedSessionId, features);
      if (!response.success) {
        throw new Error(response.error || 'No se pudo guardar la pausa.');
      }
      await loadSessions();
      setShowPauseDialog(false);
      toast.success(features.length > 0 ? 'Pausa aplicada a esta sesión.' : 'Bot reanudado en esta sesión.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo guardar la pausa.');
    } finally {
      setSavingPause(false);
    }
  };

  const handleSavePauseMode = () => {
    if (pauseMode === 'all') {
      void savePauseSettings(['all']);
    } else if (pauseMode === 'conversation') {
      void savePauseSettings(['autoReplies', 'menus']);
    } else {
      void savePauseSettings(customPauseFeatures);
    }
  };

  useEffect(() => {
    loadUserInfo();
  }, [currentUserId, propCurrentUser]);

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      const user = propCurrentUser || await getCurrentUser();
      setUserInfo(user);

      const [limitsResponse, statsResponse] = await Promise.all([
        getSubscriptionLimits(),
        currentUserId ? getUserStats(currentUserId) : Promise.resolve(null)
      ]);

      if (limitsResponse.success) {
        setSubscriptionLimits(limitsResponse.limits);
      }

      if (statsResponse && statsResponse.success) {
        setUserStats(statsResponse.stats);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone?: string | null) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    // Caso típico Colombia: 57 + 10 dígitos (ej: 573001112233)
    if (digits.length === 12 && digits.startsWith('57')) {
      const cc = digits.slice(0, 2);
      const part1 = digits.slice(2, 5);
      const part2 = digits.slice(5, 8);
      const part3 = digits.slice(8);
      return `+${cc} ${part1} ${part2} ${part3}`;
    }
    // Fallback genérico: +<todo>
    return `+${digits}`;
  };

  // Filter logs by current user
  const userLogs = currentUserId
    ? logs.filter(l => !l.userId || l.userId === currentUserId)
    : logs; // Show all logs if no userId (backward compatibility)

  // Use DB count as source of truth for sent messages (includes auto-replies and all message types)
  const sentCountFromDB = userStats?.currentMonthCount || 0;
  // Also count from logs for display (may differ if logs weren't loaded or filtered)
  const sentCountFromLogs = userLogs.filter(l => l.status === 'sent').length;
  const failedCount = userLogs.filter(l => l.status === 'failed').length;

  // Get subscription info
  const subscriptionType = userInfo?.subscription_type || 'gratuito';
  const subscriptionLimit = subscriptionLimits.find(l => l.type === subscriptionType);
  const currentMonthCount = userStats?.currentMonthCount || 0;

  // Administrador always has unlimited messages
  const isUnlimited = subscriptionType === 'administrador' || subscriptionLimit?.messages === Infinity;
  const messagesLimit = isUnlimited ? Infinity : (subscriptionLimit?.messages || 0);
  const messagesRemaining = isUnlimited ? Infinity : Math.max(0, messagesLimit - currentMonthCount);
  const usagePercentage = isUnlimited ? 0 : (messagesLimit > 0 ? (currentMonthCount / messagesLimit) * 100 : 0);

  const getSubscriptionIcon = (type: string) => {
    switch (type) {
      case 'administrador': return <Crown className="text-yellow-600" size={20} />;
      case 'pro': return <Zap className="text-blue-600" size={20} />;
      case 'elite': return <TrendingUp className="text-purple-600" size={20} />;
      default: return <Gift className="text-primary-600" size={20} />;
    }
  };

  const getSubscriptionColor = (type: string) => {
    switch (type) {
      case 'administrador': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'pro': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'elite': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-primary-100 text-primary-800 border-primary-300';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const isSubscriptionExpired = (endDate?: string) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  const handleInitializeAndScroll = () => {
    if (sessionManagerRef.current) {
      sessionManagerRef.current.createSession();
    } else {
      onInitialize(); // fallback
    }
    // Scroll to QR container after a short delay to allow QR to render
    setTimeout(() => {
      qrContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  };

  return (
    <div className="space-y-4">
      <header className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-theme-main">Resumen del Sistema</h2>
          <p className="text-theme-muted">Estado en tiempo real de tu instancia WhatyBot.</p>
        </div>
        <div
          onClick={handleInitializeAndScroll}
          className={`text-xs px-3 py-1 rounded-full cursor-pointer transition-all hover:opacity-80 ${socketStatus === 'Connected' ? 'bg-primary-100 text-primary-700' : 'bg-red-100 text-red-700'}`}
          title="Haz clic para conectar WhatsApp y generar QR"
        >
          Socket: {socketStatus || (isConnected ? 'Conectado' : 'Desconectado')}
        </div>
      </header>

      {/* Per-session bot pause controls */}
      <section className={`overflow-hidden rounded-2xl border shadow-sm ${pauseIsActive ? 'border-amber-300 bg-amber-50/70' : 'border-theme bg-theme-card'}`}>
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex min-w-0 items-start gap-4">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${pauseIsActive ? 'bg-amber-100 text-amber-700' : 'bg-primary-100 text-primary-700'}`}>
              {pauseIsActive ? <Pause size={22} /> : <Bot size={23} />}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-bold text-theme-main">Control del bot</h3>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${pauseIsActive ? 'bg-amber-200/70 text-amber-800' : 'bg-primary-100 text-primary-700'}`}>
                  {pauseIsActive ? 'Pausado' : 'Activo'}
                </span>
              </div>
              <p className="mt-1 text-sm text-theme-muted">
                {selectedSession
                  ? `Sesión ${selectedSession.phoneNumber ? `+${selectedSession.phoneNumber}` : selectedSession.sessionId.slice(0, 12)}`
                  : 'Selecciona una sesión de WhatsApp para administrar sus automatizaciones.'}
              </p>
              {pauseIsActive && (
                <p className="mt-2 text-xs font-medium text-amber-900/80">
                  {pausedFeatures.includes('all')
                    ? 'Toda la actividad del bot está detenida.'
                    : pausedFeatures.map((feature) => pauseFeatureLabels[feature] || feature).join(' · ')}
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {pauseIsActive && (
              <button
                type="button"
                onClick={() => void savePauseSettings([])}
                disabled={!selectedSessionId || savingPause}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-theme bg-theme-card px-4 py-2.5 text-sm font-semibold text-theme-main transition hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Play size={15} /> Reanudar
              </button>
            )}
            <button
              type="button"
              onClick={openPauseDialog}
              disabled={!selectedSessionId || savingPause}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 ${pauseIsActive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-900 hover:bg-slate-800'}`}
            >
              <SlidersHorizontal size={15} /> {pauseIsActive ? 'Configurar pausa' : 'Pausar bot'}
            </button>
          </div>
        </div>
      </section>

      {showPauseDialog && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !savingPause) setShowPauseDialog(false);
          }}
        >
          <section role="dialog" aria-modal="true" aria-labelledby="bot-pause-title" className="w-full max-w-2xl overflow-hidden rounded-3xl border border-white/15 bg-theme-card shadow-2xl">
            <header className="flex items-start justify-between gap-4 border-b border-theme px-6 py-5 sm:px-7">
              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-primary-700">
                  <Bot size={15} /> Control por sesión
                </div>
                <h3 id="bot-pause-title" className="text-xl font-black text-theme-main">Pausar automatizaciones</h3>
                <p className="mt-1 text-sm text-theme-muted">
                  {selectedSession?.phoneNumber ? `Cuenta +${selectedSession.phoneNumber}` : 'La configuración solo afecta a la sesión seleccionada.'}
                </p>
              </div>
              <button type="button" onClick={() => setShowPauseDialog(false)} disabled={savingPause} aria-label="Cerrar" className="rounded-xl p-2 text-theme-muted transition hover:bg-theme-base hover:text-theme-main disabled:opacity-50">
                <X size={18} />
              </button>
            </header>

            <div className="space-y-3 px-6 py-5 sm:px-7">
              <button
                type="button"
                role="radio"
                aria-checked={pauseMode === 'all'}
                onClick={() => setPauseMode('all')}
                className={`flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition ${pauseMode === 'all' ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-100' : 'border-theme hover:border-amber-200 hover:bg-theme-base/60'}`}
              >
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${pauseMode === 'all' ? 'border-amber-600 bg-amber-600 text-white' : 'border-slate-300 text-transparent'}`}><Check size={12} strokeWidth={3} /></span>
                <span className="flex-1">
                  <span className="block text-sm font-bold text-theme-main">Pausar toda la sesión</span>
                  <span className="mt-1 block text-xs leading-relaxed text-theme-muted">Detiene respuestas, menús, campañas, mensajes programados y cualquier envío desde esta cuenta.</span>
                </span>
                <Pause className="mt-0.5 shrink-0 text-amber-700" size={17} />
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={pauseMode === 'conversation'}
                onClick={() => setPauseMode('conversation')}
                className={`flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition ${pauseMode === 'conversation' ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-100' : 'border-theme hover:border-primary-200 hover:bg-theme-base/60'}`}
              >
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${pauseMode === 'conversation' ? 'border-primary-600 bg-primary-600 text-white' : 'border-slate-300 text-transparent'}`}><Check size={12} strokeWidth={3} /></span>
                <span className="flex-1">
                  <span className="block text-sm font-bold text-theme-main">Solo auto-respuestas y menús</span>
                  <span className="mt-1 block text-xs leading-relaxed text-theme-muted">Deja en pausa el chatbot conversacional y conserva disponibles los demás tipos de envío.</span>
                </span>
                <MessageCircle className="mt-0.5 shrink-0 text-primary-700" size={17} />
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={pauseMode === 'custom'}
                onClick={() => setPauseMode('custom')}
                className={`flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition ${pauseMode === 'custom' ? 'border-slate-400 bg-slate-50 ring-2 ring-slate-100' : 'border-theme hover:border-slate-300 hover:bg-theme-base/60'}`}
              >
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${pauseMode === 'custom' ? 'border-slate-700 bg-slate-700 text-white' : 'border-slate-300 text-transparent'}`}><Check size={12} strokeWidth={3} /></span>
                <span className="flex-1">
                  <span className="block text-sm font-bold text-theme-main">Elegir elementos</span>
                  <span className="mt-1 block text-xs leading-relaxed text-theme-muted">Selecciona los módulos que quieres detener en esta sesión.</span>
                </span>
                <SlidersHorizontal className="mt-0.5 shrink-0 text-slate-600" size={17} />
              </button>

              {pauseMode === 'custom' && (
                <div className="ml-2 space-y-2 border-l-2 border-slate-200 pl-4 pt-1">
                  {pauseFeatureOptions.map(({ id, label, description, icon: Icon }) => {
                    const checked = customPauseFeatures.includes(id);
                    return (
                      <label key={id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${checked ? 'border-primary-200 bg-primary-50/70' : 'border-theme hover:bg-theme-base/60'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setCustomPauseFeatures((current) => checked ? current.filter((feature) => feature !== id) : [...current, id])}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <Icon className={`mt-0.5 shrink-0 ${checked ? 'text-primary-700' : 'text-theme-muted'}`} size={17} />
                        <span>
                          <span className="block text-sm font-semibold text-theme-main">{label}</span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-theme-muted">{description}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <footer className="flex flex-col-reverse gap-2 border-t border-theme bg-theme-base/50 px-6 py-4 sm:flex-row sm:justify-between sm:px-7">
              <button type="button" onClick={() => void savePauseSettings([])} disabled={savingPause || !pauseIsActive} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-theme-muted transition hover:bg-theme-base hover:text-theme-main disabled:cursor-not-allowed disabled:opacity-40">
                Reanudar todo
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowPauseDialog(false)} disabled={savingPause} className="rounded-xl border border-theme bg-theme-card px-4 py-2.5 text-sm font-semibold text-theme-main transition hover:bg-theme-base disabled:opacity-50">Cancelar</button>
                <button type="button" onClick={handleSavePauseMode} disabled={savingPause || (pauseMode === 'custom' && customPauseFeatures.length === 0)} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">
                  {savingPause ? 'Guardando…' : 'Aplicar pausa'}
                </button>
              </div>
            </footer>
          </section>
        </div>
      )}

      {/* Subscription Info Card */}
      {!loading && userInfo && subscriptionLimit && (
        <div className={`bg-theme-card rounded-xl shadow-sm border-2 p-4 ${getSubscriptionColor(subscriptionType).split(' ')[0]} border-${subscriptionType === 'administrador' ? 'yellow' : subscriptionType === 'pro' ? 'blue' : subscriptionType === 'elite' ? 'purple' : 'green'}-300`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-theme-card/50 rounded-xl shadow-sm border border-white/60">
                {getSubscriptionIcon(subscriptionType)}
              </div>
              <div>
                <h3 className="text-xl font-black text-theme-main capitalize leading-none tracking-tight">{subscriptionType}</h3>
                <p className="text-sm font-medium text-theme-muted mt-2">
                  {userInfo.subscription_start_date && userInfo.subscription_end_date ? (
                    <span className="flex items-center gap-1.5">
                      Válido hasta: {formatDate(userInfo.subscription_end_date)}
                      {isSubscriptionExpired(userInfo.subscription_end_date) && (
                        <span className="ml-1 text-red-600 font-bold">(Expirada)</span>
                      )}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <InfinityIcon size={14} className="opacity-70" />
                      Suscripción permanente
                    </span>
                  )}
                </p>
              </div>
            </div>
            {subscriptionLimit.price > 0 && (
              <div className="text-right">
                <p className="text-xs text-theme-muted font-medium">Precio</p>
                <p className="text-lg font-bold text-theme-main">${subscriptionLimit.price}/mes</p>
              </div>
            )}
          </div>

          <div className="mt-4 bg-theme-card/50 p-4 rounded-xl border border-white/70 shadow-inner">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-bold text-theme-muted uppercase tracking-wide">Mensajes consumidos</span>
              <span className="text-sm font-black text-theme-main">
                <span className={`px-3 py-1 rounded-lg border ${isUnlimited ? 'text-primary-700 bg-primary-100/80 border-primary-200' :
                  usagePercentage >= 90 ? 'text-red-700 bg-red-100/80 border-red-200' :
                    'text-indigo-700 bg-indigo-100/80 border-indigo-200'
                  }`}>
                  {currentMonthCount} / {isUnlimited ? 'ILIMITADO' : messagesLimit.toLocaleString()}
                </span>
              </span>
            </div>

            {isUnlimited ? (
              <div className="flex items-center gap-4 py-1">
                <div className="flex items-center gap-1.5 text-xs font-black text-primary-700 bg-primary-100/50 px-3 py-1 rounded-lg border border-primary-200 shadow-sm">
                  <CircleCheckBig size={16} />
                  <span>SIN LÍMITES ACTIVOS</span>
                </div>
                <div className="h-6 w-px bg-slate-300/40"></div>
                <p className="text-xs text-theme-muted font-bold italic">Tu cuenta no tiene restricciones de envío de mensajes.</p>
              </div>
            ) : (
              <div className="space-y-2 pt-1">
                <div className="w-full bg-slate-200/50 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${usagePercentage >= 90 ? 'bg-red-500' :
                      usagePercentage >= 70 ? 'bg-orange-500' :
                        'bg-primary-500'
                      }`}
                    style={{ width: `${Math.min(100, usagePercentage)}%` }}
                  />
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`font-bold ${messagesRemaining === 0 ? 'text-red-600' : 'text-theme-muted'}`}>
                    {messagesRemaining === 0 ? 'Límite alcanzado' : `${messagesRemaining} mensajes restantes`}
                  </span>
                  <span className="text-theme-muted font-medium">
                    {usagePercentage.toFixed(1)}% utilizado
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {/* Card: Client Status */}
        <div
          onClick={handleInitializeAndScroll}
          className="bg-theme-card p-4 rounded-xl shadow-sm border border-theme flex items-center gap-3 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]"
          title="Haz clic para gestionar sesiones de WhatsApp"
        >
          <div className={`p-3 rounded-full flex-shrink-0 ${isConnected ? 'bg-primary-100 text-primary-600' : 'bg-red-100 text-red-600'}`}>
            <Wifi size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Cliente</p>
            <h3 className={`text-lg font-black truncate ${isConnected ? 'text-primary-600' : 'text-red-600'}`}>
              {isConnected ? 'CONECTADO' : 'DESCONECTADO'}
            </h3>
          </div>
        </div>

        {/* Card: Sent Messages */}
        <div className="bg-theme-card p-4 rounded-xl shadow-sm border border-theme flex items-center gap-3">
          <div className="p-3 rounded-full bg-blue-100 text-blue-600 flex-shrink-0">
            <CheckCircle size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Enviados</p>
            <div className="flex items-baseline gap-1.5">
              <h3 className="text-lg font-black text-theme-main">{sentCountFromDB}</h3>
              {sentCountFromDB !== sentCountFromLogs && (
                <span className="text-[10px] text-slate-400 font-medium">({sentCountFromLogs} rec.)</span>
              )}
            </div>
          </div>
        </div>

        {/* Card: Failed Messages */}
        <div className="bg-theme-card p-4 rounded-xl shadow-sm border border-theme flex items-center gap-3">
          <div className="p-3 rounded-full bg-red-100 text-red-600 flex-shrink-0">
            <XCircle size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Fallidos</p>
            <h3 className="text-lg font-black text-red-600">{failedCount}</h3>
          </div>
        </div>

        {/* Card: Queue Status */}
        <div className="bg-theme-card p-4 rounded-xl shadow-sm border border-theme flex items-center gap-3">
          <div className="p-3 rounded-full bg-orange-100 text-orange-600 flex-shrink-0">
            <Activity size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Cola</p>
            <h3 className="text-lg font-black text-theme-main">{userLogs.length > 0 ? 'ACTIVA' : 'INACTIVA'}</h3>
          </div>
        </div>
      </div>

      {/* Connection Area / QR Code */}
      <div id="session-manager-area" className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div ref={qrContainerRef} className="">
          <SessionManager ref={sessionManagerRef} currentUser={propCurrentUser} />
        </div>

        <div className="bg-theme-card rounded-xl shadow-sm border border-theme overflow-hidden">
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between px-1 min-h-[32px]">
                <h3 className="font-semibold text-theme-main">Registros Recientes</h3>
              </div>
              <div className="h-[350px] overflow-y-auto pr-2 -mr-2">
                {userLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 italic">No hay registros recientes</div>
                ) : (
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-theme-muted uppercase bg-theme-base">
                      <tr>
                        <th className="px-6 py-3">Hora</th>
                        <th className="px-6 py-3">Tipo</th>
                        <th className="px-6 py-3">Destino</th>
                        <th className="px-6 py-3">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userLogs.slice().reverse().map((log) => {
                        const messageType = log.messageType || 'single';
                        const getTypeLabel = (type: string) => {
                          switch (type) {
                            case 'auto-reply': return { label: 'Auto-respuesta', color: 'bg-purple-100 text-purple-700' };
                            case 'bulk': return { label: 'Masivo', color: 'bg-blue-100 text-blue-700' };
                            case 'group': return { label: 'Grupo', color: 'bg-indigo-100 text-indigo-700' };
                            case 'media': return { label: 'Multimedia', color: 'bg-pink-100 text-pink-700' };
                            default: return { label: 'Individual', color: 'bg-slate-100 text-theme-main' };
                          }
                        };
                        const typeInfo = getTypeLabel(messageType);

                        return (
                          <tr key={log.id} className="border-b border-theme hover:bg-theme-base">
                            <td className="px-6 py-4 font-mono text-theme-muted">
                              {log.timestamp.toLocaleTimeString()}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-theme-main">{log.target}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${log.status === 'sent' ? 'bg-primary-100 text-primary-700' :
                                log.status === 'failed' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                {log.status === 'sent' ? 'ENVIADO' : log.status === 'failed' ? 'FALLIDO' : 'PENDIENTE'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
